// Siparis satirindaki mesaj gorevleri: Siparis geldi -> Kargo -> Teslim.
// Durum yalnizca sunucudaki taslaklardan okunur (satir basina Etsy cagrisi yok).
// Taslak yoksa ve olay gerceklestiyse tek tikla olusturulur; ileride toplu
// gorev olusturma ayni uca dayanacak.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Package, Truck, PackageCheck, Check, Plus, Loader2, AlertTriangle } from "lucide-react";
import { clsx } from "clsx";
import { authHeaders, getBackendUrl } from "../../lib/http";

export type OrderTaskKind = "order_received" | "order_shipped" | "order_delivered";
export interface OrderTask { id: string; status: string; channel: string; error: string | null }
export type OrderTasksMap = Partial<Record<OrderTaskKind, OrderTask>>;

const STEPS: Array<{ kind: OrderTaskKind; label: string; icon: typeof Package }> = [
  { kind: "order_received", label: "Sipariş mesajı", icon: Package },
  { kind: "order_shipped", label: "Kargo mesajı", icon: Truck },
  { kind: "order_delivered", label: "Teslim mesajı", icon: PackageCheck },
];

async function createDraft(receiptId: number, kind: OrderTaskKind) {
  const res = await fetch(`${getBackendUrl()}/api/buyer-messages/order-draft`, {
    method: "POST", headers: authHeaders(), body: JSON.stringify({ receiptId, kind }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || "Taslak oluşturulamadı");
}

export default function OrderTasks({
  receiptId, tasks, paid, shipped, delivered, canceled,
}: {
  receiptId: number;
  tasks?: OrderTasksMap;
  paid: boolean;
  shipped: boolean;
  delivered: boolean;
  canceled: boolean;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<OrderTaskKind | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (canceled) return null;

  // Olay gerceklesti mi (gorev acilabilir mi)? Kargo mesaji kargodan once de
  // hazirlanabilir: takip numarasi girilince Etsy bildirimiyle gider.
  const ready: Record<OrderTaskKind, boolean> = {
    order_received: paid,
    order_shipped: paid,
    order_delivered: delivered,
  };

  const create = async (kind: OrderTaskKind) => {
    setBusy(kind); setErr(null);
    try {
      await createDraft(receiptId, kind);
      qc.invalidateQueries({ queryKey: ["etsy-receipts"] });
      qc.invalidateQueries({ queryKey: ["buyer-messages"] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const chip = "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]";
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      {STEPS.map(({ kind, label, icon: Icon }) => {
        const t = tasks?.[kind];
        const to = `/etsy/messages?receipt=${receiptId}`;
        if (t?.status === "sent") {
          return (
            <span key={kind} className={clsx(chip, "border-emerald-200 bg-emerald-50 text-emerald-700")} title="Gönderildi">
              <Check className="h-3 w-3" /> {label}
            </span>
          );
        }
        if (t?.status === "draft") {
          const viaTracking = kind === "order_shipped" && t.channel === "api" && !shipped;
          return (
            <Link key={kind} to={to} className={clsx(chip, "border-orange-300 bg-orange-50 font-medium text-orange-700 hover:bg-orange-100")}
              title={viaTracking ? "Takip numarası girilince Etsy bildirimiyle gider" : "Onay bekliyor — mesajlara git"}>
              <Icon className="h-3 w-3" /> {label}: {viaTracking ? "takip no ile" : "onay bekliyor"}
            </Link>
          );
        }
        if (t?.status === "failed") {
          return (
            <Link key={kind} to={to} className={clsx(chip, "border-red-200 bg-red-50 text-red-700 hover:bg-red-100")} title={t.error ?? "Hata"}>
              <AlertTriangle className="h-3 w-3" /> {label}: hata
            </Link>
          );
        }
        if (t?.status === "dismissed") {
          return (
            <Link key={kind} to={to} className={clsx(chip, "border-gray-200 text-gray-400 line-through hover:bg-gray-50")} title="Yoksayıldı">
              <Icon className="h-3 w-3" /> {label}
            </Link>
          );
        }
        if (ready[kind]) {
          return (
            <button key={kind} onClick={() => create(kind)} disabled={busy !== null}
              className={clsx(chip, "border-dashed border-gray-300 text-gray-600 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700")}
              title="Bu sipariş için taslak oluştur">
              {busy === kind ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} {label}
            </button>
          );
        }
        return (
          <span key={kind} className={clsx(chip, "border-gray-100 text-gray-300")}
            title={kind === "order_delivered" ? "Teslim edilince açılır" : "Ödeme bekleniyor"}>
            <Icon className="h-3 w-3" /> {label}
          </span>
        );
      })}
      {err && <span className="text-[11px] text-red-600">{err}</span>}
    </div>
  );
}
